import * as React from 'react';
import { type KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { cn } from '../../lib/cn';
import { useTabStripOverflow } from '../../hooks/use_tab_strip_overflow';

export interface StatusTab {
  /** Unique tab identifier — passed back in `onChange` */
  id: string;
  /** Display label — caller provides already-translated string */
  label: string;
  /** Optional count badge shown beside the label */
  count?: number;
}

export interface StatusTabFilterProps {
  /** Ordered list of tabs to render */
  tabs: StatusTab[];
  /** ID of the currently active tab */
  activeTab: string;
  /** Called when the user selects a different tab */
  onChange: (id: string) => void;
  /** Optional className for the root element */
  className?: string;
}

/**
 * StatusTabFilter — Tier 1 glass design system component.
 *
 * A pill-style tab strip with optional count badges. Sits above list tables
 * on every list page to filter rows by document status (All, Draft, Sent, …).
 *
 * The track uses `glass-soft` and each tab uses `glass-strong` when active.
 * Count chips use `bg-white/60` background with `var(--accent)` text.
 * Zero-count chips are rendered at reduced opacity.
 *
 * All label text is passed as props — callers supply already-translated strings.
 *
 * Keyboard: ArrowRight/ArrowLeft navigate between tabs (roving tabindex, ARIA
 * tablist auto-activation pattern). Home/End jump to first/last tab.
 *
 * Reference: ~/Downloads/quotes.html lines 240-246, 401-411.
 */
export const StatusTabFilter = React.forwardRef<
  HTMLDivElement,
  StatusTabFilterProps
>(function StatusTabFilter({ tabs, activeTab, onChange, className }, ref) {
  const btn_refs = useRef<(HTMLButtonElement | null)[]>([]);

  // `.nv-tab-strip` paints a right-edge scroll-fade mask so overflowing tab
  // rows hint "scroll for more" on narrow (phone) widths. That mask also fades
  // the active tab's underline whenever the active tab is the last one and the
  // strip does NOT overflow (the common desktop case), making the indicator
  // trail off instead of reading as a crisp bar like every other active tab in
  // the app. Only keep the mask when the strip genuinely scrolls; otherwise add
  // `nv-tab-strip--no-fade` to drop it — see `useTabStripOverflow` for the
  // shared fit-check (also used by the Tax Hub's statutory tab strip).
  const strip_ref = useRef<HTMLDivElement | null>(null);
  const fits = useTabStripOverflow(strip_ref, [tabs]);

  const set_refs = useCallback(
    (el: HTMLDivElement | null) => {
      strip_ref.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  // Keep the active tab fully visible in the horizontally-scrolling strip.
  // `block/inline: 'nearest'` only moves the strip the minimum distance
  // needed to bring the active tab into view (a tab already fully visible
  // causes no movement at all), and `behavior: 'smooth'` animates it gently
  // rather than jumping. This is the one deliberate, controlled scroll —
  // native browser scroll mechanisms (focus-scroll-into-view on click,
  // Chrome's scroll anchoring reacting to the active tab's own font-weight
  // change widening it) are suppressed elsewhere (the onMouseDown handler
  // below, and `overflow-anchor: none` on .nv-tab-strip in glass.css) so
  // they don't fight this one.
  //
  // Deliberately depends on `activeTab` alone, not `tabs`: every call site
  // builds `tabs` inline via `.map()`, so its array identity changes on
  // every parent re-render (a count-badge refetch, a keystroke in an
  // unrelated filter). `scrollIntoView` walks every scrollable ancestor
  // including the page itself, so keying this effect off `tabs` yanks the
  // whole page back to the tab strip on any such re-render, even when the
  // user has since scrolled away. `tabs_ref` gives the effect the current
  // tabs list without making it a re-run trigger.
  const tabs_ref = useRef(tabs);
  tabs_ref.current = tabs;
  useEffect(() => {
    const idx = tabs_ref.current.findIndex((tab) => tab.id === activeTab);
    if (idx === -1) return;
    const btn = btn_refs.current[idx];
    // Guarded on scrollIntoView existence — happy-dom/jsdom omit it (see the
    // same guard on AccountCombobox/TaxRateCombobox), and this component is
    // rendered in test environments across multiple product apps.
    if (btn && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeTab]);

  function handle_key_down(e: KeyboardEvent<HTMLButtonElement>, idx: number) {
    let next_idx: number | null = null;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next_idx = (idx + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      next_idx = (idx - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      next_idx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next_idx = tabs.length - 1;
    }

    if (next_idx !== null) {
      onChange(tabs[next_idx].id);
      btn_refs.current[next_idx]?.focus();
    }
  }

  return (
    <div
      ref={set_refs}
      className={cn('nv-tab-strip', fits && 'nv-tab-strip--no-fade', className)}
      role="tablist"
      data-testid="status-tab-filter"
    >
      {tabs.map((tab, idx) => {
        const is_active = tab.id === activeTab;
        const has_count = tab.count !== undefined;
        const is_zero = tab.count === 0;

        return (
          <button
            key={tab.id}
            ref={(el) => {
              btn_refs.current[idx] = el;
            }}
            role="tab"
            aria-selected={is_active}
            tabIndex={is_active ? 0 : -1}
            onClick={() => {
              onChange(tab.id);
              // onMouseDown below pre-empts the browser's native
              // focus-on-mousedown, so a mouse/touch selection would
              // otherwise leave no tab focused at all — breaking the roving
              // tabindex contract (Arrow/Home/End in handle_key_down need a
              // focused button to compute `idx` from) until the user tabs
              // back into the strip. Re-focus here, but with
              // `preventScroll: true` so this doesn't reintroduce the very
              // scroll-into-view jump the mousedown handler exists to
              // suppress.
              btn_refs.current[idx]?.focus({ preventScroll: true });
            }}
            // Prevents the browser's native "scroll the newly-focused
            // element into view" behaviour, which fires whenever a click
            // focuses a button outside its scrollable ancestor's visible
            // area (this tab strip scrolls horizontally on narrow
            // screens) — independent of and not fixed by removing
            // scroll-snap from .nv-tab-strip (glass.css) alone. Focus is
            // the browser's default action for mousedown, so it has to be
            // pre-empted here; by the time onClick/onFocus runs the scroll
            // has typically already happened. Only mousedown (mouse/touch)
            // is intercepted — real keyboard Tab/arrow navigation (via
            // handle_key_down below, which calls .focus() directly) never
            // fires mousedown, so keyboard users still get the tab
            // scrolled into view, which they need to see what's focused.
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onKeyDown={(e) => {
              handle_key_down(e, idx);
            }}
            className={`nv-tab${is_active ? ' is-active' : ''}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              lineHeight: 1,
              // Keep each tab on one line and prevent flex from squeezing them
              // on narrow (phone) widths — the .nv-tab-strip track scrolls
              // horizontally instead of cramping the labels / count badges.
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
            data-testid={`status-tab-${tab.id}`}
          >
            {tab.label}
            {has_count && (
              <span
                data-testid={`status-tab-count-${tab.id}`}
                style={{
                  fontSize: '11px',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '9999px',
                  // Derived from --accent, not a baked navy literal: sibling
                  // product apps (CostPro terracotta, WagePro green) re-point
                  // --accent, and a hardcoded rgba(31,58,95,…) left the active
                  // tab's count chip navy inside an otherwise-rethemed strip.
                  background: is_active
                    ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                    : 'rgba(15, 23, 42, 0.06)',
                  color: is_active ? 'var(--accent)' : 'var(--ink-muted)',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.2,
                  minWidth: '18px',
                  textAlign: 'center',
                  opacity: is_zero ? 0.5 : 1,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});
